/**
 * Debounce a value -- returns the input after `delayMs` milliseconds have
 * elapsed without further changes. Useful for search inputs that drive API
 * calls: the user can type freely while the request is only fired once they
 * pause.
 *
 * Authored for FR3 admin user-management search (see
 * /execute-feature-prp dev-platform-stabilisation Phase 3.5). No prior
 * useDebouncedValue or useDebounce hook existed in the frontend at PRP
 * design time; this is the first.
 *
 * Usage:
 *   const [searchQuery, setSearchQuery] = useState('')
 *   const debouncedQuery = useDebouncedValue(searchQuery, 250)
 *   useEffect(() => { fetch(debouncedQuery) }, [debouncedQuery])
 */
import { useEffect, useState } from 'react'

export function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebounced(value)
    }, delayMs)
    return () => {
      clearTimeout(handle)
    }
  }, [value, delayMs])

  return debounced
}
